import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import $ from 'jquery';
import React, {useCallback, useRef} from 'react';

import {NucleotideType} from 'biojs/BioConstants';
import {Strand} from 'graphql-client/__generated/graphqlGlobalTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE} from 'ui-library/Colors';
import {ElementMouseEvent} from 'util/Types';

export interface PrimerRowProps {
  meltingTemp: string;
  name: string;
  nucleotideType: NucleotideType;
  position: number;
  strand: Strand;
  active: boolean;
  selected: boolean;
  partnerSelected: boolean;
  onPrimerRowClick: (shiftKeySelected: boolean) => void;
  checked: boolean;
  onPrimerToggleCheck: (checked: boolean) => void;
  bases: string;
  oligoId: string;
  displayLabel: string;
}

export default function PrimerRow({
  meltingTemp,
  nucleotideType,
  name,
  position,
  strand,
  active,
  selected,
  partnerSelected,
  onPrimerRowClick,
  checked,
  onPrimerToggleCheck,
  displayLabel,
}: PrimerRowProps): JSX.Element {
  const mouseDownRef = useRef(false);

  const handleMouseDown = (e: ElementMouseEvent): void => {
    mouseDownRef.current = true;

    if (e.shiftKey) {
      // Don't allow shift selecting in multiple rows (this prevents shift selecting in single rows too,
      // which is okay since there's no cursor anyway).
      e.preventDefault();
    }
  };

  const handleMouseLeave = (): void => {
    if (mouseDownRef.current) {
      $(window).one('mouseup', () => {
        mouseDownRef.current = false;
      });

      // Clear the selection so that you can't make a text selection across multiple primers.
      // This still doesn't perfectly work when PrimerInfo is showing (a primer is selected),
      // not exactly sure why. It's a pretty minor edge case, so ignoring it for now.
      if (typeof window.getSelection === 'function') {
        window.getSelection()!.removeAllRanges();
      }
      mouseDownRef.current = false;
    }
  };

  const onClickCheckbox = useCallback((e: ElementMouseEvent) => {
    // Clicking the checkbox should not count as clicking the primer row. In PrimerPanelView's case, this
    // is to make sure the sequence's selection isn't updated when the checkbox is changed.
    e.stopPropagation();
  }, []);

  const onChangeCheckbox = useCallback(
    (e) => {
      onPrimerToggleCheck(e.target.checked);
    },
    [onPrimerToggleCheck]
  );

  return (
    <tr
      className={classNames('selectable', {
        active,
        selected,
        'partner-selected': partnerSelected && !selected,
      })}
      onClick={(e) => {
        onPrimerRowClick(e.shiftKey);
      }}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
    >
      <td>
        <input
          type="checkbox"
          checked={checked}
          onClick={onClickCheckbox}
          onChange={onChangeCheckbox}
          className={css(styles.checkbox)}
        />
      </td>
      <td className="name" {...getTestProps({component: 'PrimerRow', element: 'name'})}>
        {displayLabel}
      </td>
      <td>{nucleotideType}</td>
      <td>
        {strand === Strand.FORWARD ? '+' : <>&ndash;</>}/ {position}
      </td>
      <td>
        <div>{meltingTemp}</div>
      </td>
      <td />
    </tr>
  );
}

export const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});
